import React from 'react'
import classNames from 'classnames'
import { Download } from 'lucide-react'
import {
  ImageData,
  getAspectRatioClass,
  getFixedHeightImageStyle,
} from '../utils/imageUtils'
import { downloadFile } from '../../../../utils/download'

interface ImageItemProps {
  image: ImageData
  onClick?: () => void
}

export const ImageItem: React.FC<ImageItemProps> = ({ image, onClick }) => {
  const aspectClass = getAspectRatioClass(image.aspectRatio)
  const fixedHeightStyle = getFixedHeightImageStyle(image.aspectRatio)

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (image.url) {
      const filename = `ai-image-${image.id}.jpg`
      await downloadFile(image.url, filename, {
        title: 'AI 생성 이미지',
        text: 'AI로 생성한 이미지를 저장하거나 공유하세요',
      })
    }
  }

  // 생성 중인 상태들 (pending, processing)
  if (image.type === 'pending' || image.type === 'processing') {
    return (
      <div
        className={classNames(aspectClass, 'relative overflow-hidden')}
        title={`생성 중... ${image.prompt} (${image.aspectRatio})`}
      >
        {/* 갤러리 스타일의 스켈레톤 */}
        <div className="relative h-full w-full bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
          <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
          <div className="absolute inset-0 bg-white opacity-10 mix-blend-multiply"></div>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
              repeating-linear-gradient(45deg, 
                transparent, 
                transparent 8px, 
                rgba(255,255,255,0.1) 8px, 
                rgba(255,255,255,0.1) 12px
              ),
              repeating-linear-gradient(-45deg, 
                transparent, 
                transparent 6px, 
                rgba(255,255,255,0.08) 6px, 
                rgba(255,255,255,0.08) 10px
              )
            `,
            }}
          ></div>
        </div>

        {/* 생성중 태그 */}
        <div className="absolute left-2 top-2 z-10 rounded bg-[rgba(39,39,39,0.7)] px-2 py-1">
          <span className="text-xs font-bold text-white">
            {image.type === 'processing' ? '생성중' : '대기중'}
          </span>
        </div>
      </div>
    )
  }

  // 실패 상태
  if (image.type === 'failed') {
    return (
      <div
        className={classNames(aspectClass, 'relative overflow-hidden')}
        title={`생성 실패: ${image.prompt} (${image.aspectRatio})`}
      >
        {/* 실패 배경 */}
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-red-300 to-red-500">
          <div className="text-center text-white">
            <div className="mb-1 text-xs font-bold">생성 실패</div>
            <div className="text-xs opacity-80">다시 시도해주세요</div>
          </div>
        </div>

        {/* 실패 태그 */}
        <div className="absolute left-2 top-2 z-10 rounded bg-[rgba(220,38,38,0.8)] px-2 py-1">
          <span className="text-xs font-bold text-white">실패</span>
        </div>
      </div>
    )
  }

  // 완성된 이미지
  return (
    <div
      className={classNames(
        aspectClass,
        'group relative flex cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-zinc-800/30 shadow-xl',
      )}
      title={`${image.prompt} (${image.aspectRatio})`}
      onClick={onClick}
    >
      <img
        src={image.url}
        alt={`Result ${image.id}`}
        className="max-h-full max-w-full object-contain"
        loading="lazy"
      />

      {/* 다운로드 버튼 - 호버시에만 표시 */}
      {image.url && (
        <div className="absolute bottom-3 right-3 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={handleDownload}
            className="rounded-md border border-[rgba(255,255,255,0.2)] bg-[rgba(55,59,66,0.7)] p-3 transition-colors hover:bg-[rgba(55,59,66,0.9)]"
            title="이미지 다운로드"
          >
            <Download size={16} className="text-white" />
          </button>
        </div>
      )}
    </div>
  )
}
